/**
 * STARSEED-CAPMARKETS-134 — admin surface for the unresolved partner-account
 * queue.
 *
 * 114 shipped the resolution service and every endpoint this page needs; this
 * is the surface that lets an admin do the job without curl and a bearer token.
 *
 * The design constraint that shapes everything here: **the admin's job on this
 * page is to look at an account they have never seen and decide, correctly,
 * which member it belongs to.** Money attached to the wrong member is the worst
 * outcome in this pack — invisible to the person harmed and plausible to
 * everyone else. So this page deliberately offers no help in choosing:
 *
 *   - No suggested member, no similarity ordering, no pre-selection. The
 *     picker is alphabetical and starts empty (`orderMembersForPicker` cannot
 *     rank by resemblance — it never receives the account label).
 *   - A 409 renders as a conflict showing BOTH member ids, and `force` is a
 *     separate deliberate act, never a retry button.
 *   - Detach names the member and states that their balance will disappear.
 *   - An absent figure renders "Unknown", never "$0.00" (`describeMoney`).
 *
 * An empty queue and a broken queue look identical and mean opposite things,
 * so a load failure renders an explicit error rather than an empty table.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { AlertTriangle, Link2, RefreshCw, Unlink } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { formatAsOf } from "../lib/heroBalance";
import {
  describeMoney,
  describeWait,
  memberLabel,
  orderMembersForPicker,
  parseConflictResponse,
} from "../lib/partnerAccountQueue";

/**
 * The API's page size for `/users` — `routes/users.py` clamps `limit` to 500 and
 * sorts `created_at` DESCENDING. So a club with more than 500 users loses its
 * OLDEST members from this picker first, which is precisely the population most
 * likely to hold a partner account. A silently-short list is the dangerous
 * shape: the admin sees an apparently-complete roster, cannot find the right
 * member, and the nearest wrong name is right there. We can't paginate from
 * here without a search endpoint (CAPMARKETS-147), so we say so out loud.
 */
const MEMBER_PAGE_LIMIT = 500;

/**
 * Coerce an API error body into something safe to hand a toast.
 *
 * FastAPI returns a LIST of error objects for a 422, and sonner renders its
 * argument as a React child — so passing `detail` through raw throws "Objects
 * are not valid as a React child" from the Toaster and the admin sees no error
 * at all, only a page that appears to have done nothing.
 *
 * Being safe is not the same as being useful. Falling straight back to a
 * generic string avoids the crash and also throws away the only text that says
 * WHICH field the server rejected, which on this page means an admin who
 * cannot attach an account and cannot tell you why. So the list is flattened
 * to `field: msg`, and the fallback is kept for the case where flattening
 * yields nothing (Copilot, PR #2401).
 */
const errorText = (e, fallback) => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((d) => {
        const msg = typeof d?.msg === "string" ? d.msg : "";
        if (!msg) return "";
        // `loc` is ["body", "<field>"]; the tail is the field an admin can act on.
        const field = Array.isArray(d?.loc) ? d.loc[d.loc.length - 1] : undefined;
        return field ? `${field}: ${msg}` : msg;
      })
      .filter(Boolean);
    return parts.length ? parts.join("; ") : fallback;
  }
  if (detail && typeof detail === "object" && typeof detail.message === "string") {
    return detail.message;
  }
  return fallback;
};

const Money = ({ value }) => {
  const d = describeMoney(value);
  if (!d.known) {
    return (
      <span data-testid="money-unknown" className="text-foreground-soft italic">
        Unknown
      </span>
    );
  }
  return <span className="font-mono">{d.text}</span>;
};

/**
 * Member picker, reason and Attach button for one account. One component for
 * both lists on this page, so the rules on it — empty and first by default, no
 * suggested member (CAPMARKETS-114) — cannot drift between them.
 */
const AttachControls = ({
  account, members, selection, setSelection, notes, setNotes, busy, onAttach,
}) => {
  const ref = account.partner_account_ref;
  return (
    <div className="mt-3 flex flex-wrap items-end gap-2">
      <label className="text-sm">
        <span className="mb-1 block text-xs text-foreground-soft">Member</span>
        <select
          data-testid={`member-select-${ref}`}
          className="rounded-md border border-border bg-background px-2 py-1 text-sm"
          value={selection[ref] || ""}
          onChange={(e) => setSelection((s) => ({ ...s, [ref]: e.target.value }))}
        >
          {/* Empty by default and FIRST. No pre-selection: the right member is
              a human judgement, and a default makes the wrong answer one click
              away. */}
          <option value="">Choose a member…</option>
          {members.map((m) => (
            <option key={m.id} value={m.id}>
              {m.full_name || m.email}
            </option>
          ))}
        </select>
      </label>

      <label className="text-sm">
        <span className="mb-1 block text-xs text-foreground-soft">Why this member?</span>
        <input
          data-testid={`note-${ref}`}
          className="w-64 rounded-md border border-border bg-background px-2 py-1 text-sm"
          placeholder="e.g. login confirmed on the 2026-08-04 statement"
          value={notes[ref] || ""}
          onChange={(e) => setNotes((n) => ({ ...n, [ref]: e.target.value }))}
        />
      </label>

      <Button data-testid={`attach-${ref}`} disabled={busy} onClick={() => onAttach(account)}>
        <Link2 className="mr-2 h-4 w-4" />
        Attach
      </Button>
    </div>
  );
};

const AdminPartnerAccountsPage = () => {
  const [queue, setQueue] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [selection, setSelection] = useState({});
  const [notes, setNotes] = useState({});
  const [conflict, setConflict] = useState(null);
  const [busyRef, setBusyRef] = useState(null);
  const [membersTruncated, setMembersTruncated] = useState(false);
  // STARSEED-CAPMARKETS-325 — accounts AT THE PARTNER that nobody has attached,
  // read live. Kept apart from the statement queue above: that one only knows
  // accounts a statement has mentioned. `unattachedError` is its own, because
  // a partner outage must not hide the queue, and an unreadable list must not
  // read as "nothing unattached".
  const [unattached, setUnattached] = useState([]);
  const [unattachedError, setUnattachedError] = useState(null);
  // Its own loading state, so our own records never wait on the partner: a
  // partner outage takes ~31s to fail (a 15s timeout and one retry).
  const [unattachedLoading, setUnattachedLoading] = useState(true);
  const [unreadableRows, setUnreadableRows] = useState(0);
  const latestUnattached = useRef(0);

  const now = Date.now();

  /**
   * Forget everything the operator typed or chose for one account.
   *
   * Called after a successful attach AND after a detach. Detach is the
   * correction flow for a WRONG attach: the account returns to the unresolved
   * queue, and if its old selection survived, the row would come back with the
   * just-detached member pre-selected and one click away from being
   * re-attached. That is exactly the pre-selection AC5 forbids, arriving by the
   * back door. The stale note would be re-sent too, so the audit trail would
   * carry the old justification for the new decision.
   */
  const clearRef = useCallback((ref) => {
    setSelection((s) => {
      if (!(ref in s)) return s;
      const next = { ...s };
      delete next[ref];
      return next;
    });
    setNotes((n) => {
      if (!(ref in n)) return n;
      const next = { ...n };
      delete next[ref];
      return next;
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    // A conflict card describes one specific attempt against one specific view
    // of the data. Once we reload, it is stale — and it carries a live override
    // button, so leaving it up is an offer to force a decision that no longer
    // has the context it was made in.
    setConflict(null);
    try {
      const [q, m, u] = await Promise.all([
        api.get("/admin/partner-accounts/unresolved"),
        api.get("/admin/partner-accounts/mappings"),
        api.get("/users", { params: { limit: MEMBER_PAGE_LIMIT } }),
      ]);
      const rawUsers = Array.isArray(u.data) ? u.data : [];
      setQueue(q.data?.accounts || []);
      setMappings(m.data?.mappings || []);
      // Measured on the RAW response, before the role filter — the server's cap
      // applies to all users, so filtering first would hide the truncation
      // behind a client count that is legitimately smaller.
      setMembersTruncated(rawUsers.length >= MEMBER_PAGE_LIMIT);
      setMembers(rawUsers.filter((x) => x && x.role === "client"));
    } catch (e) {
      // An empty table would read as "nothing waiting", which is the opposite
      // of what a failed load means.
      setLoadError(errorText(e, "Could not load the partner-account queue."));
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * STARSEED-CAPMARKETS-325 — the live list of accounts at the partner that
   * nobody has attached. Loaded on its own so the queue and the attached list,
   * which come from our own records, never wait on the partner; and a failed or
   * malformed read is this section's error, never an empty list.
   */
  const loadUnattached = useCallback(async () => {
    const loadId = ++latestUnattached.current;
    setUnattachedLoading(true);
    try {
      const un = await api.get("/admin/partner-accounts/unattached");
      if (loadId !== latestUnattached.current) return;
      if (!Array.isArray(un?.data?.accounts)) {
        setUnattached([]);
        setUnreadableRows(0);
        setUnattachedError("The partner's account list came back in an unexpected shape.");
      } else {
        setUnattached(un.data.accounts);
        setUnreadableRows(Number(un.data.unreadable_rows) || 0);
        setUnattachedError(null);
      }
    } catch (e) {
      if (loadId !== latestUnattached.current) return;
      setUnattached([]);
      setUnreadableRows(0);
      setUnattachedError(errorText(e, "Could not read the partner's account list."));
    } finally {
      if (loadId === latestUnattached.current) setUnattachedLoading(false);
    }
  }, []);

  const refreshAll = useCallback(() => {
    load();
    loadUnattached();
  }, [load, loadUnattached]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const orderedMembers = useMemo(() => orderMembersForPicker(members), [members]);

  // The same REF is shown once, in the statement queue. A statement keyed by a
  // display name ("Ge Ve") and the login the partner lists are different refs,
  // each needing its own mapping, so both appear; a label is never a join key
  // (CAPMARKETS-133).
  const unattachedRows = useMemo(() => {
    const queued = new Set(queue.map((a) => a.partner_account_ref));
    return unattached.filter((a) => !queued.has(a.partner_account_ref));
  }, [queue, unattached]);

  /**
   * Attach an account to a member.
   *
   * `memberId` is an EXPLICIT parameter rather than something this function
   * reads out of `selection` on every call, and that is the whole point
   * (`/code-review`, PR #2401). The override path re-enters here from the
   * conflict card, which was rendered from a decision the operator already
   * made and is still reading on screen. If the member were re-read from the
   * dropdown at override time, the queue rows behind the card — which stay
   * rendered and interactive — could be changed between the 409 and the
   * override, and the write would silently retarget: the card says "This
   * attach names Jamie Fischer" while the POST carries George.
   *
   * That is the single path on this page that bypasses the server's refusal to
   * pick a winner, so it is the last place that may act on anything other than
   * what the operator was shown. The override passes `conflict.memberId` — the
   * member captured at the moment the conflict was raised.
   */
  const attach = async (account, { force = false, memberId: explicitMemberId } = {}) => {
    const ref = account.partner_account_ref;
    const memberId = explicitMemberId || selection[ref];
    if (!memberId) {
      toast.error("Choose a member first");
      return;
    }
    setBusyRef(ref);
    try {
      await api.post("/admin/partner-accounts/mappings", {
        partner_account_ref: ref,
        member_id: memberId,
        partner_account_label: account.partner_account_label,
        note: notes[ref] || undefined,
        force,
      });
      toast.success(
        `${account.partner_account_label || ref} attached to ${memberLabel(members, memberId)}`,
      );
      setConflict(null);
      clearRef(ref);
      // Now claimed, so it leaves the partner list here rather than costing
      // another read of the whole partner book.
      setUnattached((rows) => rows.filter((a) => a.partner_account_ref !== ref));
      await load();
    } catch (e) {
      const parsed = parseConflictResponse(e);
      if (parsed) {
        // NOT a toast. The server refused to pick a winner; the screen must
        // show who disagrees with whom.
        setConflict({ account, memberId, ...parsed });
        return;
      }
      toast.error(errorText(e, "Could not attach the account"));
    } finally {
      setBusyRef(null);
    }
  };

  const detach = async (mapping) => {
    const who = mapping.member_name || mapping.member_email || mapping.member_id;
    const ok = window.confirm(
      `Detach ${mapping.partner_account_label || mapping.partner_account_ref} from ${who}?\n\n` +
        `Every snapshot this account claimed will be detached with it, and ${who}'s ` +
        `partner-held balance will disappear from their dashboard. The figures are not ` +
        `deleted — the account returns to the unresolved queue.`,
    );
    if (!ok) return;
    setBusyRef(mapping.partner_account_ref);
    try {
      await api.delete(
        `/admin/partner-accounts/mappings/${encodeURIComponent(mapping.partner_account_ref)}`,
      );
      toast.success(`Detached from ${who} — the account is back in the queue`);
      // The account is about to reappear in the unresolved queue. It must come
      // back with an EMPTY picker, not pre-set to the member we just detached.
      clearRef(mapping.partner_account_ref);
      await load();
      // A detached login may belong back on the partner list; that answer
      // needs the partner, so it loads on its own without holding the page.
      loadUnattached();
    } catch (e) {
      toast.error(errorText(e, "Could not detach the account"));
    } finally {
      setBusyRef(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Partner accounts</h1>
            <p className="text-sm text-foreground-muted">
              Accounts seen in a partner statement that are not yet attached to a member.
              Every day an account waits here is a day a member&rsquo;s money is missing
              from their dashboard.
            </p>
          </div>
          <Button
            data-testid="queue-refresh"
            variant="outline"
            onClick={refreshAll}
            disabled={loading}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>

        {loadError && (
          <Card>
            <CardContent className="p-4">
              <div data-testid="queue-load-error" className="flex items-start gap-2 text-danger">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <p className="font-medium">The queue could not be loaded.</p>
                  <p className="text-sm text-foreground-muted">
                    {String(loadError)} — this is not the same as an empty queue.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {conflict && (
          <Card>
            <CardContent className="p-4">
              <div data-testid="attach-conflict" className="space-y-3">
                <div className="flex items-start gap-2 text-warning">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div>
                    <p className="font-medium">Conflicting attachment — nothing was saved.</p>
                    <p className="text-sm text-foreground-muted">{conflict.message}</p>
                  </div>
                </div>
                {conflict.conflicts.map((c, i) => (
                  <div
                    key={`${c.partner_account_ref}-${i}`}
                    className="rounded-md border border-border bg-background-subtle p-3 text-sm"
                  >
                    <p className="font-mono text-foreground">{c.partner_account_ref}</p>
                    <p className="text-foreground-muted">
                      This attach names{" "}
                      <strong data-testid="conflict-mapped">
                        {memberLabel(members, c.mapped_member_id)}
                      </strong>
                      ; the cached partner row ({c.source}) names{" "}
                      <strong data-testid="conflict-cached">
                        {memberLabel(members, c.cached_member_id)}
                      </strong>
                      .
                    </p>
                    <p className="mt-1 text-xs text-foreground-soft">{c.detail}</p>
                  </div>
                ))}
                <p className="text-sm text-foreground-muted">
                  Resolve the disagreement at the source if you can. Overriding records
                  both the override and the conflict it overrode in the audit trail.
                </p>
                <p data-testid="conflict-target" className="text-sm text-foreground">
                  Overriding attaches{" "}
                  {conflict.account.partner_account_label || conflict.account.partner_account_ref}{" "}
                  to <strong>{memberLabel(members, conflict.memberId)}</strong>.
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setConflict(null)}>
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    data-testid="conflict-force"
                    disabled={busyRef === conflict.account.partner_account_ref}
                    onClick={() =>
                      // `memberId` explicitly — never whatever the dropdown
                      // happens to hold now. See the docblock on `attach`.
                      attach(conflict.account, { force: true, memberId: conflict.memberId })
                    }
                  >
                    Override and attach anyway
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="p-4">
            <h2 className="mb-3 text-lg font-medium text-foreground">
              Waiting to be attached
            </h2>

            {loading && <p className="text-sm text-foreground-muted">Loading…</p>}

            {!loading && !loadError && membersTruncated && (
              <div
                data-testid="members-truncated"
                className="mb-3 flex items-start gap-2 rounded-md border border-warning bg-background-subtle p-3 text-sm text-foreground"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
                <span>
                  The member list is capped at {MEMBER_PAGE_LIMIT} and shows the most
                  recently created accounts first, so <strong>older members may be
                  missing from the picker</strong>. If the member you want is not listed,
                  do not attach to a similar name — the account can safely keep waiting.
                </span>
              </div>
            )}

            {!loading && !loadError && queue.length === 0 && (
              <p data-testid="queue-empty" className="text-sm text-foreground-muted">
                Nothing waiting — every account seen in a statement is attached to a member.
              </p>
            )}

            {/* Gated on `!loadError` as well as `!loading` (Copilot, PR #2401):
                after a failed REFRESH the previous rows are still in state, and
                rendering them under an error banner shows stale figures the UI
                itself has just called untrusted. Same argument as the empty-vs-
                broken distinction — an admin must not act on numbers we cannot
                currently stand behind. */}
            {!loading &&
              !loadError &&
              queue.map((a) => {
                const wait = describeWait(a, now);
                return (
                  <div
                    key={a.partner_account_ref}
                    data-testid="queue-row"
                    className="mb-3 rounded-lg border border-border p-3"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <div>
                        <span className="font-medium text-foreground">
                          {a.partner_account_label || a.partner_account_ref}
                        </span>
                        <span className="ml-2 font-mono text-xs text-foreground-soft">
                          {a.partner_account_ref}
                        </span>
                      </div>
                      <Badge variant="outline" data-testid="queue-wait">
                        Unattached {wait.label}
                        {a.first_seen_as_of ? ` · since ${formatAsOf(a.first_seen_as_of)}` : ""}
                      </Badge>
                    </div>

                    <div className="mt-2 grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
                      <div>
                        <div className="text-xs text-foreground-soft">Balance</div>
                        <Money value={a.balance} />
                      </div>
                      <div>
                        <div className="text-xs text-foreground-soft">Equity</div>
                        <Money value={a.equity} />
                      </div>
                      <div>
                        <div className="text-xs text-foreground-soft">Snapshots</div>
                        <span>{a.snapshots}</span>
                      </div>
                      <div>
                        <div className="text-xs text-foreground-soft">Confidence</div>
                        <span>{a.confidence || "—"}</span>
                      </div>
                    </div>

                    <AttachControls
                      account={a}
                      members={orderedMembers}
                      selection={selection}
                      setSelection={setSelection}
                      notes={notes}
                      setNotes={setNotes}
                      busy={busyRef === a.partner_account_ref}
                      onAttach={attach}
                    />
                  </div>
                );
              })}
          </CardContent>
        </Card>

        {/* STARSEED-CAPMARKETS-325 — every account at the partner that no
            mapping claims, read live, so an account opened today shows here on
            the next refresh, before any statement mentions it. A ref the
            statement queue above already lists is shown there only. */}
        <Card>
          <CardContent className="p-4">
            <h2 className="mb-1 text-lg font-medium text-foreground">
              At the partner, not attached to anyone
            </h2>
            <p className="mb-3 text-sm text-foreground-muted">
              Read live from the partner. Accounts holding money are listed first. Nothing
              here suggests an owner: attaching is your decision.
            </p>

            {unattachedLoading && (
              <p data-testid="unattached-loading" className="text-sm text-foreground-muted">
                Reading the partner&rsquo;s account list…
              </p>
            )}

            {!unattachedLoading && !loadError && !unattachedError && unreadableRows > 0 && (
              <div
                data-testid="unattached-unreadable"
                className="mb-3 flex items-start gap-2 text-sm text-warning"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  {unreadableRows} row{unreadableRows === 1 ? "" : "s"} from the partner could
                  not be read, so this list may be incomplete.
                </span>
              </div>
            )}

            {!unattachedLoading && !loadError && unattachedError && (
              <div
                data-testid="unattached-load-error"
                className="flex items-start gap-2 text-sm text-danger"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  {String(unattachedError)} This says nothing about which accounts exist.
                </span>
              </div>
            )}

            {!unattachedLoading && !loading && !loadError && !unattachedError
              && unattachedRows.length === 0 && (
              <p data-testid="unattached-empty" className="text-sm text-foreground-muted">
                Every account at the partner is attached to a member or already waiting above.
              </p>
            )}

            {!unattachedLoading &&
              !loadError &&
              !unattachedError &&
              unattachedRows.map((a) => (
                <div
                  key={a.partner_account_ref}
                  data-testid="unattached-row"
                  data-account={a.partner_account_ref}
                  className="mb-3 rounded-lg border border-border p-3"
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <div>
                      <span className="font-medium text-foreground">
                        {a.partner_account_label || a.partner_account_ref}
                      </span>
                      <span className="ml-2 font-mono text-xs text-foreground-soft">
                        {a.partner_account_ref}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {a.carries_money && (
                        <Badge variant="outline" data-testid="unattached-carries-money">
                          Holds money
                        </Badge>
                      )}
                      {a.created_at && formatAsOf(a.created_at) && (
                        <Badge variant="outline">Opened {formatAsOf(a.created_at)}</Badge>
                      )}
                    </div>
                  </div>

                  <div className="mt-2 grid grid-cols-2 gap-2 text-sm sm:grid-cols-3">
                    <div>
                      <div className="text-xs text-foreground-soft">In the account now</div>
                      <Money value={a.in_account} />
                    </div>
                    <div>
                      <div className="text-xs text-foreground-soft">Net of its movements</div>
                      <Money value={a.movements_net} />
                      {a.movements_net_partial && (
                        <div data-testid="unattached-net-partial" className="text-xs text-foreground-soft">
                          Some amounts were not reported by the partner.
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="text-xs text-foreground-soft">Movements</div>
                      <span>
                        {a.movement_count === null || a.movement_count === undefined
                          ? "Unknown"
                          : a.movement_count}
                        {a.last_movement_at && formatAsOf(a.last_movement_at)
                          ? ` · last ${formatAsOf(a.last_movement_at)}`
                          : ""}
                      </span>
                    </div>
                  </div>

                  <AttachControls
                    account={a}
                    members={orderedMembers}
                    selection={selection}
                    setSelection={setSelection}
                    notes={notes}
                    setNotes={setNotes}
                    busy={busyRef === a.partner_account_ref}
                    onAttach={attach}
                  />
                </div>
              ))}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <h2 className="mb-3 text-lg font-medium text-foreground">Attached accounts</h2>
            {!loading && !loadError && mappings.length === 0 && (
              <p className="text-sm text-foreground-muted">No accounts are attached yet.</p>
            )}
            {/* Gated on `!loading` as well as `!loadError`: during an in-flight
                refresh these rows are last-load's data carrying live Detach
                buttons, and detaching is destructive to a member's visible
                balance. Same argument as the queue above. */}
            {!loading && !loadError && mappings.map((m) => (
              <div
                key={m.partner_account_ref}
                data-testid="mapping-row"
                className="mb-2 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border p-3"
              >
                <div>
                  <span className="font-medium text-foreground">
                    {m.partner_account_label || m.partner_account_ref}
                  </span>
                  <span className="ml-2 font-mono text-xs text-foreground-soft">
                    {m.partner_account_ref}
                  </span>
                  <div className="text-sm text-foreground-muted">
                    {m.member_name || m.member_email || m.member_id}
                    {m.note ? <span className="text-foreground-soft"> — {m.note}</span> : null}
                  </div>
                  {/* STARSEED-CAPMARKETS-356 AC8: an attach the watch made on the
                      partner's label, told apart from a person's. Detach undoes it. */}
                  {m.attached_automatically ? (
                    <Badge variant="outline" className="mt-1" data-testid="mapping-auto">
                      Attached automatically
                    </Badge>
                  ) : null}
                </div>
                <Button
                  variant="outline"
                  data-testid={`detach-${m.partner_account_ref}`}
                  disabled={busyRef === m.partner_account_ref}
                  onClick={() => detach(m)}
                >
                  <Unlink className="mr-2 h-4 w-4" />
                  Detach
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminPartnerAccountsPage;
